if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const variantId = this.variantIdInput ? this.variantIdInput.value : null;

        this.buildAddRequestConfig(config)
          .then((requestConfig) => fetch(`${routes.cart_add_url}`, requestConfig))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // Errors we raise ourselves carry copy that is safe to show.
            if (e && e.displayMessage) {
              this.handleErrorMessage(e.message);
            } else {
              console.error(e);
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /*
       * Resolves the /cart/add.js request body.
       *
       * "single" keeps Dawn's original FormData request byte for byte, so the
       * normal Add to Cart path carries no bundle logic at all.
       *
       * "bundle" pre-flights the cart and issues one multi-item add. It never
       * prices anything: the gift is made free by a Shopify automatic discount.
       */
      async buildAddRequestConfig(config) {
        const offerSelector = this.offerSelector;
        const isBundle = Boolean(offerSelector) && offerSelector.selectedOffer === 'bundle';
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;

        if (!isBundle) {
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (sections) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
          return config;
        }

        const offer = window.RedelisseOffer;
        const variantId = this.variantIdInput ? this.variantIdInput.value : null;

        // Fail the whole submission rather than promise a gift and add half a
        // bundle. The gift product must be explicitly configured and available.
        if (!offer || !variantId || !offerSelector.giftReady) {
          throw this.offerError(offerSelector.giftErrorMessage);
        }

        const quantityInput = this.form.querySelector('[name="quantity"]');
        const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');

        const parentItem = { id: Number(variantId), quantity: Number(quantityInput?.value) || 1 };
        if (sellingPlanInput && sellingPlanInput.value) {
          parentItem.selling_plan = Number(sellingPlanInput.value);
        }

        // Pre-flight is the primary guard against a second gift unit.
        let giftAlreadyInCart;
        try {
          giftAlreadyInCart = await offer.hasGift();
        } catch (error) {
          throw this.offerError(window.cartStrings?.error || offerSelector.giftErrorMessage);
        }

        // Shopify renders a multi-item add in reverse request order, so the gift
        // is sent FIRST to make the main product sit above it in the cart.
        const items = [];

        if (!giftAlreadyInCart) {
          items.push({
            id: Number(offerSelector.giftVariantId),
            quantity: 1,
            properties: offer.giftProperties(variantId, offerSelector.parentProductId),
          });
        }

        items.push(parentItem);

        const body = { items };
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(body);
        return config;
      }

      offerError(message) {
        const error = new Error(message || 'This offer is currently unavailable.');
        error.displayMessage = true;
        return error;
      }

      get offerSelector() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return null;
        return document.querySelector(`offer-selector[data-section-id="${sectionId}"]`);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
